import AppSettings from '../AppSettings';
// Librairies
import Axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'universal-cookie';
import i18n from '../locales/i18n';
// Utils
import RealtimeUtil from '../utils/RealtimeUtil';
import { showLoadingToast, showToast } from '../utils/ToastsUtil';
import DatesUtil from '../utils/DatesUtil';
import TreesUtil from '../utils/TreesUtil';
import { treeStore, UnsupportedTreeFormatError } from '../utils/trees/TreeStore';
import { canUseFieldWrite, submitFieldWrite } from '../utils/offline/FieldWrite';
import { getOfflineServices } from '../utils/offline/OfflineServices';
import { newLocalNumericId } from '../utils/offline/IdMap';
import { addElementHistory, createElement, createElements, removeElements, updateElement, updateElements } from '../utils/offline/ElementWrites';
import { isOnline } from '../utils/offline/Connectivity';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class TreesService {
    static addTree(tree, action) {
        tree.id = uuidv4();
        tree.properties.treeId = tree.id;
        tree.properties.dimensions.propertiesId = tree.id;
        // Pas de this : méthode passée détachée par ProjectMap
        if (canUseFieldWrite()) return TreesService.addTreeThroughOutbox(tree, action);
        const promise = Axios.post(BACKEND_ENDPOINT + 'trees/' + RealtimeUtil.getConnectionId(), tree, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            tree.properties = TreesUtil.normalizeTreeProperties(response.data.properties);
            console.log('#fetchId', tree.fetchId, response.data.fetchId);
            tree.fetchId = response.data.fetchId;
            return response;
        }, (error) => {
            if (isOnline() && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const toastId = 'tree_' + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = 'tree_' + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = 'tree_' + (action === 'adding' ? 'addition' : 'copy_paste') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    // Arbre enregistré sur l'appareil puis envoyé ; { data, isLocal } comme Axios
    static addTreeThroughOutbox(tree, action) {
        const promise = createElement('Arbre', tree).then(response => {
            if (!response.isLocal) {
                tree.properties = TreesUtil.normalizeTreeProperties(response.data.properties);
                tree.fetchId = response.data.fetchId;
            }
            return response;
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const toastId = 'tree_' + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = 'tree_' + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = 'tree_' + (action === 'adding' ? 'addition' : 'copy_paste') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static addTrees(trees, action) {
        trees.forEach(t => {
            t.id = uuidv4();
            t.properties.treeId = t.id;
            t.properties.dimensions.propertiesId = t.id;
        });
        if (canUseFieldWrite()) return TreesService.addTreesThroughOutbox(trees, action);
        const promise = Axios.post(BACKEND_ENDPOINT + 'trees/bulk/' + RealtimeUtil.getConnectionId(), trees, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            response.data.forEach(t => {
                let tree = trees.find(x => x.id === t.id);
                tree.properties = TreesUtil.normalizeTreeProperties(t.properties);
                console.log('#fetchId', tree.fetchId, t.fetchId);
                tree.fetchId = t.fetchId;
            });
            return response;
        }, (error) => {
            if (isOnline() && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const prefix = trees.length > 1 ? 'trees_' : 'tree_';
        const toastId = prefix + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = prefix + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = prefix + (action === 'adding' ? 'addition' : action === 'copyPasting' ? 'copy_paste' : 'duplication') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise, trees.length);
        return promise;
    }

    // Comme la réponse de l'API, data contient les arbres, complétés par le serveur s'il a répondu à temps
    static addTreesThroughOutbox(trees, action) {
        const promise = createElements('Arbre', trees).then(results => {
            const treesById = new Map(trees.map(tree => [tree.id, tree]));
            results.filter(result => !result.isLocal).flatMap(result => result.data).forEach(serverTree => {
                const tree = treesById.get(serverTree.id);
                if (!tree) return;
                tree.properties = TreesUtil.normalizeTreeProperties(serverTree.properties);
                tree.fetchId = serverTree.fetchId;
            });
            return { data: trees, isLocal: results.some(result => result.isLocal) };
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const prefix = trees.length > 1 ? 'trees_' : 'tree_';
        const toastId = prefix + (action === 'copyPasting' ? 'copy_pasting' : action);
        const successId = prefix + (action === 'copyPasting' ? 'copy_pasted' : action.replace('ing', 'ed'));
        const errorId = prefix + (action === 'adding' ? 'addition' : action === 'copyPasting' ? 'copy_paste' : 'duplication') + '_failed';
        showLoadingToast(toastId, successId, errorId, promise, trees.length);
        return promise;
    }

    static addTreeHistory(tree, date) {
        if (canUseFieldWrite()) return addElementHistory('Arbre', tree, date).then(response => {
            showToast(response.isLocal ? 'saved_on_device' : 'history_added');
            return response.isLocal ? undefined : response.data;
        }, () => {
            showToast('history_addition_failed');
            return;
        });
        return Axios.post(BACKEND_ENDPOINT + 'trees/history/?date=' + date.toISOString(), tree, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('history_added');
            return response.data;
        }, () => {
            if (isOnline()) showToast('history_addition_failed');
            return;
        });
    }

    static getTrees(projectId, data, { binary = AppSettings.isTreesBinaryEnabled(), keepBuffer = false } = {}) {
        performance.mark(`trees-loading-${data.page}`);

        const url = (!['localhost', 'pmcwcrxr7dvaur6kr0umz9qd'].some(url => BACKEND_ENDPOINT.includes(url)) && (data?.page || data?.page === 0)
            ? BACKEND_ENDPOINT.replace('://', '://' + (Math.floor(Math.random() * 10) + 1) + '.')
            : BACKEND_ENDPOINT) + 'trees/projects/' + projectId;

        const config = { headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` } };
        if (binary) {
            config.headers['Accept'] = 'application/octet-stream';
            config.responseType = 'arraybuffer';
        }

        return Axios.post(url, data, config).then(response => {
            performance.mark(`trees-loaded-${data.page}`);

            if (binary) {
                // Back ancien ou en-tête réécrit : réponse JSON reçue en arraybuffer
                const contentType = response.headers?.['content-type'] || response.headers?.get?.('content-type') || '';
                if (!contentType.includes('octet-stream')) {
                    const texte = new TextDecoder().decode(new Uint8Array(response.data));
                    const donnees = JSON.parse(texte);
                    if (Array.isArray(donnees?.trees))
                        for (let i = 0; i < donnees.trees.length; i++) TreesUtil.normalizeTree(donnees.trees[i]);
                    return donnees;
                }

                try {
                    const decoded = TreesService.decodeTrees(response.data);
                    if (keepBuffer) decoded.buffer = response.data;
                    return decoded;
                } catch (error) {
                    if (!(error instanceof UnsupportedTreeFormatError)) throw error;
                    // Back/front désynchronisés : repli JSON plutôt que lire les octets de travers
                    // eslint-disable-next-line no-console
                    console.warn('[Grality] Bloc binaire illisible, repli sur le JSON :', error.message);
                    return TreesService.getTrees(projectId, data, { binary: false });
                }
            }

            const trees = response.data?.trees;
            if (Array.isArray(trees))
                for (let i = 0; i < trees.length; i++) TreesUtil.normalizeTree(trees[i]);
            return response.data;
        }, () => {
            performance.mark(`trees-loaded-${data.page}`)
            if (isOnline() && !data?.cacheKey && !data?.cursor) showToast('trees_loading_failed');
            return;
        });
    }

    static decodeTrees(buffer) {
        const { chunk, createView } = treeStore.addChunk(buffer);
        const trees = new Array(chunk.rowCount);
        for (let row = 0; row < chunk.rowCount; row++) trees[row] = createView(row);
        return { missingCustomFieldIds: chunk.missingCustomFieldIds, trees };
    }

    static removeTrees(trees) {
        if (canUseFieldWrite()) return TreesService.removeTreesThroughOutbox(trees);
        const promise = Axios.delete(BACKEND_ENDPOINT + 'trees/' + RealtimeUtil.getConnectionId(), {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: trees
        }).then(response => {
            return response;
        }, (error) => {
            if (isOnline() && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const prefixe = trees.length > 1 ? 'trees' : 'tree';
        showLoadingToast(prefixe + '_deleting', prefixe + '_deleted', prefixe + '_deletion_failed', promise);
        return promise;
    }

    static removeTreesThroughOutbox(trees) {
        const promise = removeElements('Arbre', trees).catch(error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });
        const prefix = trees.length > 1 ? 'trees' : 'tree';
        showLoadingToast(prefix + '_deleting', prefix + '_deleted', prefix + '_deletion_failed', promise);
        return promise;
    }

    static updateTree(id, tree, history, action, replanting, previousFeature = null) {
        if (canUseFieldWrite()) return TreesService.updateTreeThroughOutbox(id, tree, history, action, replanting, previousFeature);
        const promise = Axios.put(BACKEND_ENDPOINT + 'trees/?connectionId=' + RealtimeUtil.getConnectionId() + '&id=' + id + '&history=' + history + '&replanting=' + replanting, tree, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (replanting) tree.properties.toCutDown = 0;
            tree.properties = TreesUtil.normalizeTreeProperties({ ...tree.properties, ...response.data.tree.properties });
            tree.properties.plantingDate = tree.properties.plantingDate ? DatesUtil.convertUTCDateToDate(tree.properties.plantingDate) : null;
            tree.properties.deathDate = tree.properties.deathDate ? DatesUtil.convertUTCDateToDate(tree.properties.deathDate) : null;

            return response;
        }, (error) => {
            if (isOnline() && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const toastId = 'tree_' + action;
        const successId = 'tree_' + action.replace('ing', 'ed');
        const errorId = 'tree_' + (action === 'updating' ? 'update'
            : action === 'restoring' ? 'restoration'
                : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static updateTreeThroughOutbox(id, tree, history, action, replanting, previousFeature) {
        const promise = updateElement('Arbre', id, tree, { history, replanting }, previousFeature).then(response => {
            if (!response.isLocal) {
                if (replanting) tree.properties.toCutDown = 0;
                tree.properties = TreesUtil.normalizeTreeProperties({ ...tree.properties, ...response.data.tree.properties });
                tree.properties.plantingDate = tree.properties.plantingDate ? DatesUtil.convertUTCDateToDate(tree.properties.plantingDate) : null;
                tree.properties.deathDate = tree.properties.deathDate ? DatesUtil.convertUTCDateToDate(tree.properties.deathDate) : null;
            }
            return response;
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const toastId = 'tree_' + action;
        const successId = 'tree_' + action.replace('ing', 'ed');
        const errorId = 'tree_' + (action === 'updating' ? 'update' : action === 'restoring' ? 'restoration' : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static bulkUpdateTrees(trees, action, previousFeatures = null) {
        if (canUseFieldWrite()) return TreesService.bulkUpdateTreesThroughOutbox(trees, action, previousFeatures);
        const promise = Axios.put(BACKEND_ENDPOINT + 'trees/bulk/' + RealtimeUtil.getConnectionId(), trees, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (Array.isArray(response?.data)) {
                response.data.forEach(feature => {
                    let tree = trees.find(tree => tree.id === feature.id);
                    if (tree) {
                        tree.properties = TreesUtil.normalizeTreeProperties({ ...tree.properties, ...feature.properties });
                        tree.properties.plantingDate = tree.properties.plantingDate ? DatesUtil.convertUTCDateToDate(tree.properties.plantingDate) : null;
                        tree.properties.deathDate = tree.properties.deathDate ? DatesUtil.convertUTCDateToDate(tree.properties.deathDate) : null;
                    }
                });
            }
            return response;
        }, (error) => {
            if (isOnline() && error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject();
        });

        const toastId = 'trees_' + action;
        const successId = 'trees_' + action.replace('ing', 'ed');
        const errorId = 'trees_' + (action === 'updating' ? 'update'
            : action === 'restoring' ? 'restoration'
                : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    // Réponse locale si un paquet n'est pas accepté dans le délai ; data contient les éléments, complétés par le serveur sinon
    static bulkUpdateTreesThroughOutbox(trees, action, previousFeatures) {
        const promise = updateElements('Arbre', trees, {}, previousFeatures).then(results => {
            const byId = new Map(trees.map(feature => [feature.id, feature]));
            results.filter(result => !result.isLocal).flatMap(result => Array.isArray(result.data) ? result.data : []).forEach(serverFeature => {
                const feature = byId.get(serverFeature.id);
                if (!feature) return;
                feature.properties = TreesUtil.normalizeTreeProperties({ ...feature.properties, ...serverFeature.properties });
                feature.properties.plantingDate = feature.properties.plantingDate ? DatesUtil.convertUTCDateToDate(feature.properties.plantingDate) : null;
                feature.properties.deathDate = feature.properties.deathDate ? DatesUtil.convertUTCDateToDate(feature.properties.deathDate) : null;
            });
            return { data: trees, isLocal: results.some(result => result.isLocal) };
        }, error => {
            if (error?.response?.data === 'Project is importing data') showToast('import_in_progress');
            return Promise.reject(error);
        });

        const toastId = 'trees_' + action;
        const successId = 'trees_' + action.replace('ing', 'ed');
        const errorId = 'trees_' + (action === 'updating' ? 'update'
            : action === 'restoring' ? 'restoration'
                : action) + '_failed';
        showLoadingToast(toastId, successId, errorId, promise);
        return promise;
    }

    static getTreeHistory(id) {
        return Axios.get(BACKEND_ENDPOINT + 'trees/history/?id=' + id, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('element_history_loading_failed');
            return;
        });
    }

    static getTreeCharts(id) {
        return Axios.get(BACKEND_ENDPOINT + 'trees/charts/?id=' + id, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('element_charts_loading_failed'); // TODO
            return;
        });
    }

    static getEssences(loadAll = false) {
        return Axios.get(BACKEND_ENDPOINT + 'trees/essences' + (loadAll ? '?loadAll=true' : ''), {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, (e) => {
            if (isOnline()) showToast('essences_loading_failed');
            return;
        });
    }

    static getCoverTypes() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/coverTypes', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('cover_types_loading_failed');
            return;
        });
    }

    static getInteractions() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/interactions', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('interactions_loading_failed');
            return;
        });
    }

    static getMicroHabitats() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/microHabitats', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('micro_habitats_loading_failed');
            return;
        });
    }

    static getRootSymptoms() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/rootSymptoms', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('root_symptoms_loading_failed');
            return;
        });
    }

    static getCollarSymptoms() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/collarSymptoms', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('collar_symptoms_loading_failed');
            return;
        });
    }

    static getTrunkSymptoms() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/trunkSymptoms', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('trunk_symptoms_loading_failed');
            return;
        });
    }

    static getBranchSymptoms() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/branchSymptoms', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('branch_symptoms_loading_failed');
            return;
        });
    }

    static getLeafSymptoms() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/leafSymptoms', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('leaf_symptoms_loading_failed');
            return;
        });
    }

    static getPathogens() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/pathogens', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('pathogens_loading_failed');
            return;
        });
    }

    static getPests() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/pests', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('pests_loading_failed');
            return;
        });
    }

    static getEpiphytes() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/epiphytes', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('epiphytes_loading_failed');
            return;
        });
    }

    static getVigors() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/vigors', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('vigors_loading_failed');
            return;
        });
    }

    static getDeathCauses() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/deathCauses', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('death_causes_loading_failed');
            return;
        });
    }

    static getHealthReviews() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/healthReviews', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('health_reviews_loading_failed');
            return;
        });
    }

    static getOntogenicStages() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/ontogenicStages', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('ontogenic_stages_loading_failed');
            return;
        });
    }

    static getArchiStages() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/archiStages', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('archi_stages_loading_failed');
            return;
        });
    }

    static getArchiStates() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/archiStates', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('archi_states_loading_failed');
            return;
        });
    }

    static getRisks() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/risks', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('risks_loading_failed');
            return;
        });
    }

    static getTippingRisks() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/tippingRisks', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('tipping_risks_loading_failed');
            return;
        });
    }

    static getOrganCalibers() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/organCalibers', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('organ_calibers_loading_failed');
            return;
        });
    }

    static getTargets() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/targets', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('targets_loading_failed');
            return;
        });
    }

    static getTreePorts() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/treePorts', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('tree_ports_loading_failed');
            return;
        });
    }

    static getPlantationTypes() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/plantationTypes', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('plantation_types_loading_failed');
            return;
        });
    }

    static getPlantationCoefficients() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/plantationCoefficients', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('plantation_coefficients_loading_failed');
            return;
        });
    }

    static getSituationCoefficients() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/situationCoefficients', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('situation_coefficients_loading_failed');
            return;
        });
    }

    static getPatrimonialCoefficients() {
        return Axios.get(BACKEND_ENDPOINT + 'trees/patrimonialCoefficients', {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) showToast('patrimonial_coefficients_loading_failed');
            return;
        });
    }

    static exportTreesFromProjectAsExcel(name, id, elementIds = [], projection) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/exportXLSX/' + id, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.ms-excel' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${id} - ${name} (${i18n.t("Arbres").toLowerCase()}).xlsx`);
            document.getElementById('modal-content').appendChild(link);
            link.click();
            document.getElementById('modal-content').removeChild(link);
            URL.revokeObjectURL(url);
            return true;
        }, () => {
            if (isOnline()) showToast('elements_xlsx_export_failed');
        });
    }

    static exportTreeAsPDF(tree, image, addPrices, photosId, colorless, toastCustomTexts, historyId = null) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/exportPDF/' + tree.id, { image, addPrices, photosId, colorless, historyId }, {
            responseType: 'arraybuffer',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            }
        }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            let filename = tree.properties.projectReference ? `${i18n.t("Fiche arbre")}_${tree.properties.projectReference}` : i18n.t("Fiche arbre");
            if (tree.properties.customReference) filename += '_' + tree.properties.customReference;
            filename += '.pdf';
            link.setAttribute('download', filename);
            document.getElementById('tasksStatus').appendChild(link);
            link.click();
            document.getElementById('tasksStatus').removeChild(link);
            URL.revokeObjectURL(url);
            showToast('pdf_export_completed', ...toastCustomTexts);
        }, () => {
            if (isOnline()) showToast('elements_pdf_export_failed');
        });
    }

    static exportTreesAsPDF(projectId, elementIds, { projection, colorless = false, addPhotos = true } = {}) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/exportAllPDF/' + projectId, { elementIds, projection, colorless, addPhotos }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            return response;
        }, () => {
            if (isOnline()) showToast('elements_pdf_export_failed');
            return;
        });
    }

    static exportAllBacklogsAsPDF(projectId, grouping) {
        return Axios.get(BACKEND_ENDPOINT + 'trees/exportAllBacklogsPDF/' + grouping + '/' + projectId, {
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`,
            }
        }).then(response => {
            return response;
        }, () => {
            if (isOnline()) showToast('backlogs_pdfs_export_failed');
        });
    }

    static exportTreesAsSHP(projectLabel, projectId, elementIds, projection) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/exportSHP/' + projectId, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (response.data?.size > 0) {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Arbres").toLowerCase()}).zip`);
                document.getElementById('modal-content').appendChild(link);
                link.click();
                document.getElementById('modal-content').removeChild(link);
                URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (isOnline()) showToast('elements_shp_export_failed');
        });
    }

    static exportTreesAsGPKG(projectLabel, projectId, elementIds, projection) {
        return Axios.post(BACKEND_ENDPOINT + 'trees/exportGPKG/' + projectId, { elementIds, projection }, {
            responseType: 'blob',
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (response.data?.size > 0) {
                const blob = new Blob([response.data], { type: 'application/geopackage+sqlite3' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${projectId} - ${projectLabel} (${i18n.t("Arbres").toLowerCase()}).gpkg`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
            return true;
        }, () => {
            if (isOnline()) showToast('elements_gpkg_export_failed');
        });
    }

    static addEssence(essence, isAdmin = false) {
        if (!isAdmin && canUseFieldWrite()) return TreesService.addEssenceThroughOutbox(essence);
        return Axios.post(BACKEND_ENDPOINT + (isAdmin ? 'admin/' : '') + 'trees/essences/', essence, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('essence_added');
            return response.data;
        }, () => {
            if (isOnline()) showToast('essence_addition_failed');
            return;
        });
    }

    // Essence temporaire à id négatif, utilisable tout de suite et gardée pour un rechargement hors-ligne ; remplacée à la synchro (SyncManager)
    static addEssenceThroughOutbox(essence) {
        const localEssence = { ...essence, id: newLocalNumericId() };
        return submitFieldWrite({
            op: { type: 'essence.create', projectId: null, target: { entity: 'essence', id: localEssence.id }, payload: { essence } },
            buildLocalData: () => [localEssence]
        }).then(async response => {
            if (!response.isLocal) {
                showToast('essence_added');
                return response.data;
            }
            // Écartée dans le délai : l'essence existait déjà sur le serveur
            const serverId = response.discarded ? await getOfflineServices().idMap.get('essence', localEssence.id) : null;
            if (serverId) return [{ ...localEssence, id: serverId }];
            try { localStorage.setItem('essences', JSON.stringify([...JSON.parse(localStorage.getItem('essences') || '[]'), localEssence])); }
            catch (error) { console.error(error); }
            return response.data;
        }, () => {
            showToast('essence_addition_failed');
            return;
        });
    }

    static updateEssences(essences) {
        return Axios.put(BACKEND_ENDPOINT + 'admin/trees/essences/', essences, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            showToast('essences_updated');
            return response.status;
        }, () => {
            if (isOnline()) showToast('essences_update_failed');
            return;
        });
    }

    static deleteEssences(ids) {
        return Axios.delete(BACKEND_ENDPOINT + 'admin/trees/essences/', {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` },
            data: { ids: ids }
        }).then(response => {
            return response.status;
        }, () => {
            showToast(ids.length > 1 ? 'essences_deletion_failed' : 'essence_deletion_failed');
            return;
        });
    }

    static updateSpwFields(projectId, layers) {
        return Axios.put(BACKEND_ENDPOINT + 'admin/trees/updateSpwFields/' + projectId, null, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            const layerMap = new Map(layers.map(layer => [layer.feature.id, layer]));
            response.data.forEach(tree => {
                const layer = layerMap.get(tree.id);
                if (layer) {
                    layer.feature.properties = TreesUtil.normalizeTreeProperties({ ...layer.feature.properties, ...tree.properties });
                    layer.feature.properties.plantingDate = layer.feature.properties.plantingDate ? DatesUtil.convertUTCDateToDate(layer.feature.properties.plantingDate) : null;
                    layer.feature.properties.deathDate = layer.feature.properties.deathDate ? DatesUtil.convertUTCDateToDate(layer.feature.properties.deathDate) : null;
                }
            });

            showToast('spw_fields_updated');
            return response.data;
        }, () => {
            showToast('spw_fields_update_failed');
            return;
        });
    }
}
